import { type TableColumnSpec } from "#blocks/Table/buildTableColumns"
// Type-only: classTypes imports CLASS_GROUPS back from this module, and a
// value import would make that cycle load-order dependent.
import { type ClassForTable } from "#customTypes/composedTypes/classTypes"
import { type DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { toSortableNumber } from "#utils/sortHelpers"

export const CLASS_TABLE_COLUMNS: TableColumnSpec[] = [
  { key: "name", width: 200 },
  { key: "curriculum", width: 200 },
  { key: "weekday", width: 90 },
  { key: "startTime", width: 90, align: "right" },
  { key: "endTime", width: 90, align: "right" },
  { key: "room", width: 130 },
  { key: "teachers", width: 180 },
  { key: "skillLevels", width: 150, truncate: 15 },
  { key: "headcount", width: 80, align: "right", title: "Count" },
  { key: "notes", width: 200 },
  { key: "semester", width: 110 },
  { key: "makeups", width: 90, align: "right" },
  { key: "tryouts", width: 90, align: "right" },
  { key: "summerCamp", width: 70, align: "center", title: "Camp" },
  { key: "lastUpdatedAt", width: 140, title: "Last Updated" },
]

export const CLASS_SEARCHABLE_FIELDS: (keyof ClassForTable)[] = [
  "name",
  "curriculum",
  "teachers",
  "skillLevels",
  "room",
] as const

export const CLASS_SORTABLE_FIELDS = [
  "name",
  "curriculum",
  "room",
  "headcount",
  "semester",
  "lastUpdatedAt",
] as const

// Headcount displays as "enrolled/capacity" (e.g. "8/12"); sort on the numeric
// enrolled count. Shared by useTableSort + buildTableColumns.
export const getClassSortValue = (
  row: ClassForTable,
  field: string
): string | number => {
  if (field === "headcount") {
    return toSortableNumber(String(row.headcount).split("/")[0])
  }
  const value = row[field]
  return typeof value === "number" ? value : String(value ?? "")
}

export enum CLASS_GROUPS {
  CORE = "CORE",
  SCULPTURE = "SCULPTURE",
  PAINTING = "PAINTING",
  DIGITAL_ART = "DIGITAL_ART",
  PORTFOLIO = "PORTFOLIO",
}

export const CLASS_GROUP_LABELS: Record<CLASS_GROUPS, string> = {
  [CLASS_GROUPS.CORE]: "Core",
  [CLASS_GROUPS.SCULPTURE]: "Sculpture",
  [CLASS_GROUPS.PAINTING]: "Painting",
  [CLASS_GROUPS.DIGITAL_ART]: "Digital Art",
  [CLASS_GROUPS.PORTFOLIO]: "Portfolio",
} as const

export const CLASS_GROUP_OPTIONS: DropdownOption[] = [
  { label: "Core", value: CLASS_GROUPS.CORE },
  { label: "Sculpture", value: CLASS_GROUPS.SCULPTURE },
  { label: "Painting", value: CLASS_GROUPS.PAINTING },
  { label: "Digital Art", value: CLASS_GROUPS.DIGITAL_ART },
  { label: "Portfolio", value: CLASS_GROUPS.PORTFOLIO },
] as const

export enum ROOMS {
  YELLOW_ROOM = "YELLOW_ROOM",
  GREEN_ROOM = "GREEN_ROOM",
  PURPLE_ROOM = "PURPLE_ROOM",
  ORANGE_ROOM = "ORANGE_ROOM",
  BLUE_ROOM = "BLUE_ROOM",
  PINK_ROOM = "PINK_ROOM",
  MAIN_ROOM_1 = "MAIN_ROOM_1",
  MAIN_ROOM_2 = "MAIN_ROOM_2",
  MAIN_ROOM_3 = "MAIN_ROOM_3",
  DIGITAL_ART_ROOM = "DIGITAL_ART_ROOM",
  PORTFOLIO_ROOM = "PORTFOLIO_ROOM",
  PAINTING_ROOM = "PAINTING_ROOM",
  ZOOM_PINK_ROOM = "ZOOM_PINK_ROOM",
  ZOOM_PORTFOLIO_ROOM = "ZOOM_PORTFOLIO_ROOM",
}

export const ROOM_LABELS: Record<ROOMS, string> = {
  [ROOMS.BLUE_ROOM]: "Blue Room",
  [ROOMS.GREEN_ROOM]: "Green Room",
  [ROOMS.ORANGE_ROOM]: "Orange Room",
  [ROOMS.PURPLE_ROOM]: "Purple Room",
  [ROOMS.YELLOW_ROOM]: "Yellow Room",
  [ROOMS.MAIN_ROOM_1]: "Main Room 1",
  [ROOMS.MAIN_ROOM_2]: "Main Room 2",
  [ROOMS.MAIN_ROOM_3]: "Main Room 3",
  [ROOMS.DIGITAL_ART_ROOM]: "Digital Art Room",
  [ROOMS.PINK_ROOM]: "Pink Room",
  [ROOMS.PORTFOLIO_ROOM]: "Portfolio Room",
  [ROOMS.PAINTING_ROOM]: "Painting Room",
  [ROOMS.ZOOM_PORTFOLIO_ROOM]: "Zoom Portfolio Room",
  [ROOMS.ZOOM_PINK_ROOM]: "Zoom Pink Room",
} as const

export const ROOM_OPTIONS = [
  { label: "Blue Room", value: ROOMS.BLUE_ROOM },
  { label: "Green Room", value: ROOMS.GREEN_ROOM },
  { label: "Orange Room", value: ROOMS.ORANGE_ROOM },
  { label: "Purple Room", value: ROOMS.PURPLE_ROOM },
  { label: "Yellow Room", value: ROOMS.YELLOW_ROOM },
  { label: "Main Room 1", value: ROOMS.MAIN_ROOM_1 },
  { label: "Main Room 2", value: ROOMS.MAIN_ROOM_2 },
  { label: "Main Room 3", value: ROOMS.MAIN_ROOM_3 },
  { label: "Digital Art Room", value: ROOMS.DIGITAL_ART_ROOM },
  { label: "Painting Room", value: ROOMS.PAINTING_ROOM },
  { label: "Pink Room", value: ROOMS.PINK_ROOM },
  { label: "Portfolio Room", value: ROOMS.PORTFOLIO_ROOM },
  { label: "Zoom Portfolio Room", value: ROOMS.ZOOM_PORTFOLIO_ROOM },
  { label: "Zoom Pink Room", value: ROOMS.ZOOM_PINK_ROOM },
]

export const ROOM_COLORS: Record<ROOMS | "FRONT_DESK_ROOM", string> = {
  [ROOMS.MAIN_ROOM_1]: "#398BCE",
  [ROOMS.MAIN_ROOM_2]: "#1a65a2",
  [ROOMS.MAIN_ROOM_3]: "#193d5a",
  [ROOMS.ZOOM_PINK_ROOM]: "#E66A9C", // pink
  [ROOMS.YELLOW_ROOM]: "#F5D000", // Trying out a new yellow
  // [ROOMS.YELLOW_ROOM]: "#E2C51D",
  [ROOMS.GREEN_ROOM]: "#44892A",
  [ROOMS.PINK_ROOM]: "#c80fbe",
  [ROOMS.PURPLE_ROOM]: "#6D22A0",
  [ROOMS.ORANGE_ROOM]: "#ed7e00",
  [ROOMS.BLUE_ROOM]: "#1727bd",
  [ROOMS.DIGITAL_ART_ROOM]: "#c100b8",
  [ROOMS.PORTFOLIO_ROOM]: "#a00000",
  [ROOMS.ZOOM_PORTFOLIO_ROOM]: "#CC3535",
  [ROOMS.PAINTING_ROOM]: "#ed7e00",
  FRONT_DESK_ROOM: "#555555",
} as const
